import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dismiss20Regular, Save20Regular } from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import {
  ApiError,
  createRentalContract,
  getRentalContractStatuses,
  type RentalContractPayload,
  type RentalContractStatus,
} from "../../arrendamentos/arrendamentosApi";
import { getProperties, type Property } from "../../imoveis/imoveisApi";
import { getTenants, type Tenant } from "../../inquilinos/inquilinosApi";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar contratos de arrendamento.";
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Não foi possível criar o contrato de arrendamento.";
};

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
const inputStyle = {
  borderRadius: "var(--radius-button)",
  fontSize: "var(--text-base)",
} as const;
const labelClass = "block text-foreground mb-2";
const labelStyle = {
  fontSize: "var(--text-caption)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

export function NovoArrendamentoView() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [statuses, setStatuses] = useState<RentalContractStatus[]>([]);

  const [propertyId, setPropertyId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthlyRent, setMonthlyRent] = useState("");
  const [paymentDueDay, setPaymentDueDay] = useState("1");
  const [deposit, setDeposit] = useState("");
  const [statusId, setStatusId] = useState("");
  const [notes, setNotes] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    getProperties()
      .then((data) => isMounted && setProperties(data))
      .catch(() => isMounted && setProperties([]));
    getTenants()
      .then((data) => isMounted && setTenants(data))
      .catch(() => isMounted && setTenants([]));
    getRentalContractStatuses()
      .then((data) => {
        if (!isMounted) return;
        setStatuses(data);
        // Estado por defeito: "ativo".
        const active = data.find((s) => s.code === "ativo") ?? data[0];
        if (active) setStatusId(String(active.id));
      })
      .catch(() => isMounted && setStatuses([]));
    return () => {
      isMounted = false;
    };
  }, []);

  const propertyOptions = useMemo<SearchableSelectOption<string>[]>(
    () => properties.map((p) => ({ value: String(p.id), label: p.name })),
    [properties],
  );

  const tenantOptions = useMemo<SearchableSelectOption<string>[]>(
    () => tenants.map((t) => ({ value: String(t.id), label: t.name })),
    [tenants],
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!propertyId) {
      setSubmitError("Selecione o imóvel.");
      return;
    }
    if (!tenantId) {
      setSubmitError("Selecione o inquilino.");
      return;
    }
    if (!startDate) {
      setSubmitError("A data de início é obrigatória.");
      return;
    }
    const rent = monthlyRent !== "" ? parseFloat(monthlyRent) : NaN;
    if (!Number.isFinite(rent)) {
      setSubmitError("Indique a renda mensal.");
      return;
    }
    const dueDay = paymentDueDay !== "" ? parseInt(paymentDueDay, 10) : NaN;
    if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 31) {
      setSubmitError("O dia de pagamento deve estar entre 1 e 31.");
      return;
    }

    setIsSaving(true);

    // Datas enviadas como YYYY-MM-DD (mesmo formato que ConfirmingView usa para os
    // seus campos DateTimeOffset — o valor cru do <input type="date">).
    const payload: RentalContractPayload = {
      propertyId: Number(propertyId),
      tenantId: Number(tenantId),
      contractNumber: contractNumber.trim() || null,
      startDate,
      endDate: endDate || null,
      monthlyRent: rent,
      paymentDueDay: dueDay,
      deposit: deposit !== "" ? parseFloat(deposit) : null,
      statusId: statusId ? Number(statusId) : null,
      notes: notes.trim() || null,
    };

    try {
      await createRentalContract(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/arrendamentos", {
      state: { toast: "Contrato de arrendamento criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/arrendamentos")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-arrendamento-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Contrato"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Contrato de Arrendamento"
        subtitle="Regista um novo contrato de arrendamento."
        actions={actions}
        backButton={{ onClick: () => navigate("/arrendamentos") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form
            id="novo-arrendamento-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Contrato
                  </h3>

                  <div>
                    <SearchableSelect<string>
                      id="propertyId"
                      label={
                        <>
                          Imóvel <span className="text-primary">*</span>
                        </>
                      }
                      labelClassName={labelClass}
                      labelStyle={labelStyle}
                      value={propertyId === "" ? null : propertyId}
                      onChange={(next) => setPropertyId(next ?? "")}
                      options={propertyOptions}
                      placeholder="Selecione o imóvel"
                      searchPlaceholder="Procurar imóvel…"
                    />
                  </div>

                  <div>
                    <SearchableSelect<string>
                      id="tenantId"
                      label={
                        <>
                          Inquilino <span className="text-primary">*</span>
                        </>
                      }
                      labelClassName={labelClass}
                      labelStyle={labelStyle}
                      value={tenantId === "" ? null : tenantId}
                      onChange={(next) => setTenantId(next ?? "")}
                      options={tenantOptions}
                      placeholder="Selecione o inquilino"
                      searchPlaceholder="Procurar inquilino…"
                    />
                  </div>

                  <div>
                    <label htmlFor="contractNumber" className={labelClass} style={labelStyle}>
                      Nº de contrato
                    </label>
                    <input
                      id="contractNumber"
                      type="text"
                      placeholder="Opcional"
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="startDate" className={labelClass} style={labelStyle}>
                      Data de início <span className="text-primary">*</span>
                    </label>
                    <input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="endDate" className={labelClass} style={labelStyle}>
                      Data de fim
                    </label>
                    <input
                      id="endDate"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Deixe vazio para contrato sem termo.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="statusId" className={labelClass} style={labelStyle}>
                      Estado
                    </label>
                    <select
                      id="statusId"
                      value={statusId}
                      onChange={(e) => setStatusId(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    >
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Condições Financeiras
                  </h3>

                  <div>
                    <label htmlFor="monthlyRent" className={labelClass} style={labelStyle}>
                      Renda mensal (€) <span className="text-primary">*</span>
                    </label>
                    <input
                      id="monthlyRent"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={monthlyRent}
                      onChange={(e) => setMonthlyRent(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="paymentDueDay" className={labelClass} style={labelStyle}>
                      Dia de pagamento
                    </label>
                    <input
                      id="paymentDueDay"
                      type="number"
                      min="1"
                      max="31"
                      step="1"
                      value={paymentDueDay}
                      onChange={(e) => setPaymentDueDay(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="deposit" className={labelClass} style={labelStyle}>
                      Caução (€)
                    </label>
                    <input
                      id="deposit"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={deposit}
                      onChange={(e) => setDeposit(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className={labelClass} style={labelStyle}>
                      Notas
                    </label>
                    <textarea
                      id="notes"
                      rows={4}
                      placeholder="Opcional"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
